import React, { useMemo } from 'react'
import styled from 'styled-components'
import { useTheme } from 'styled-components'

import {
  Carat,
  Media,
  PillButton,
  PlayIcon,
  RotateClockwise,
  Spacer,
  TypeStyle,
} from '@syconium/little-miss-figgy/dist/components'

import { useTranslation } from '../../../../app/_providers/TranslationProvider.client'
import { GalleryImageStateContainer } from '../../../../components/pages/products/ProductPage/contexts/GalleryImageStateContainer'
import { trackEvent } from '../../../../lib/analytics'
import { FitModel, Product } from '../../../../types/graphql'
import { GalleryButtonsWrapper } from '../../SwiperImageSlider/styles'
import ColorGalleryCTA from '../ColorGalleryModal/ColorGalleryCta'

import { FitModelModal } from './../FitModelModal'
import { Body, Description, LegacyButton, PillButtonCaption, StandaloneCTAWrapper } from './styles'

const category = 'model selector'

export const PlaySvg = styled(PlayIcon)`
  position: static;
  height: 10px;
  width: 10px;
  display: flex;
  justify-content: center;
  align-items: center;
  min-width: unset;
  &::after {
    position: static;
    transform: none;
  }
`

export const FitModelCaption: React.FC<{
  colorCount: number
  activeFitModel: FitModel | undefined
  activeProduct: Product
  selectFitModel?: (fitModel: FitModel) => void
  showVideoCta: boolean
  showColorGalleryCta: boolean
}> = ({
  activeFitModel,
  activeProduct,
  selectFitModel,
  showVideoCta: enableVideoCta,
  showColorGalleryCta,
  colorCount,
}) => {
  const { fitModelCount } = activeProduct
  const {
    fitModel: strings,
    favorites: { selectSize },
    pages: {
      product: {
        gallery: { seeItLive },
      },
    },
  } = useTranslation()

  const onRequestClose = () => setIsModalOpen(false)
  const [buttonClicked, setButtonClicked] = React.useState(false)
  const [isModalOpen, setIsModalOpen] = React.useState(false)

  const { allAssets, showImageZoom } = GalleryImageStateContainer.useContainer()
  const videoAssetIndex = useMemo(() => {
    return allAssets.findIndex(asset => {
      return asset.kind === 'catwalk'
    })
  }, [allAssets])

  const showVideoCta = enableVideoCta && videoAssetIndex > -1
  const showFitModelCta = !!(fitModelCount && fitModelCount > 1)

  const onlyCtaAvailableIsFitModelCta = showFitModelCta && !showVideoCta && !showColorGalleryCta
  const onlyCtaAvailableIsVideoCta = showVideoCta && !showFitModelCta && !showColorGalleryCta

  const videoCTA = useMemo(() => {
    return (
      <>
        {showVideoCta && (
          <PillButton
            onClick={() => {
              showImageZoom(videoAssetIndex)
            }}
            allCaps={false}
            {...trackEvent(
              onlyCtaAvailableIsVideoCta
                ? {
                    category,
                    action: 'open video modal from standalone cta',
                    label: `see it live standalone catwalk`,
                  }
                : {
                    category,
                    action: 'open video modal',
                    label: 'see it live catwalk',
                  }
            )}
          >
            <PlaySvg />
            <Spacer height={1} width={4} />
            <PillButtonCaption>{seeItLive}</PillButtonCaption>
          </PillButton>
        )}
      </>
    )
  }, [showVideoCta, onlyCtaAvailableIsVideoCta, seeItLive, showImageZoom, videoAssetIndex])

  const colorGalleryCTA = useMemo(() => {
    return showColorGalleryCta ? (
      <ColorGalleryCTA
        handle={activeProduct.handle || ''}
        minimize={!!activeFitModel?.description}
        colorCount={colorCount}
      />
    ) : null
  }, [showColorGalleryCta, activeProduct.handle, activeFitModel?.description, colorCount])

  const SwitchModelButton = () => (
    <PillButton
      onClick={() => {
        setButtonClicked(true)
        setIsModalOpen(true)
      }}
      {...trackEvent({
        category,
        action: 'open fit modal',
        label: 'switch model',
      })}
      allCaps={false}
    >
      <Carat rotateClockwise={RotateClockwise.Quarter} />
      <Spacer height={1} width={4} />
      <PillButtonCaption>{selectSize}</PillButtonCaption>
    </PillButton>
  )

  const { spacing } = useTheme()
  if (onlyCtaAvailableIsVideoCta) {
    return (
      <>
        <StandaloneCTAWrapper $bottomPadding={spacing(6)}>
          <PillButton
            variant='black-on-white'
            onClick={() => {
              showImageZoom(videoAssetIndex)
            }}
            allCaps={false}
            {...trackEvent({
              category,
              action: 'open video modal from standalone cta',
              label: `see it live standalone catwalk`,
            })}
          >
            <PlaySvg />
            <Spacer height={1} width={4} />
            <PillButtonCaption>{seeItLive}</PillButtonCaption>
          </PillButton>
        </StandaloneCTAWrapper>

        <Body variant={onlyCtaAvailableIsFitModelCta ? 'model-selector-only' : 'default'}>
          <GalleryButtonsWrapper>
            <Media greaterThanOrEqual='md'>{videoCTA}</Media>
            {colorGalleryCTA}
          </GalleryButtonsWrapper>
        </Body>
      </>
    )
  }

  return (
    <Body variant={onlyCtaAvailableIsFitModelCta ? 'model-selector-only' : 'default'}>
      {onlyCtaAvailableIsFitModelCta ? (
        <TypeStyle.BodyNano>{activeFitModel?.description}</TypeStyle.BodyNano>
      ) : (
        <Description>{activeFitModel?.description}</Description>
      )}

      <>
        {onlyCtaAvailableIsFitModelCta ? (
          <>
            <LegacyButton
              onClick={() => {
                setButtonClicked(true)
                setIsModalOpen(true)
              }}
              {...trackEvent({
                category,
                action: 'open fit modal',
                label: 'switch model legacy',
              })}
            >
              {strings.switchModel}
            </LegacyButton>
          </>
        ) : (
          <GalleryButtonsWrapper>
            {showFitModelCta && <SwitchModelButton />}
            {videoCTA}
            {colorGalleryCTA}
          </GalleryButtonsWrapper>
        )}

        {buttonClicked && selectFitModel && activeFitModel && (
          <FitModelModal
            activeFitModel={activeFitModel}
            activeProduct={activeProduct}
            isModalOpen={isModalOpen}
            onRequestClose={onRequestClose}
            selectFitModel={selectFitModel}
          />
        )}
      </>
    </Body>
  )
}
